import { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import {
  UserGroupIcon,
  PlusIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserPlusIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline"
import {
  listGroups,
  addGroup,
  removeGroup,
  renameGroup,
  addGroupMember,
  removeGroupMember,
} from "./controller"
import DataTable from "aldoo-ra/DataTable"

const AVATAR_COLORS = [
  "#e53935", "#d81b60", "#8e24aa", "#5e35b1",
  "#1e88e5", "#039be5", "#00897b", "#43a047",
  "#fb8c00", "#f4511e", "#6d4c41", "#546e7a",
]
function getAvatarColor(str) {
  if (!str) return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function Avatar({ contact }) {
  const [imgError, setImgError] = useState(false)
  const avatarSrc = contact?.avatar && !imgError ? cmsUrl(contact.avatar) : null
  const initial = (contact?.name || "?")[0].toUpperCase()
  if (avatarSrc) {
    return (
      <img src={avatarSrc} alt="" onError={() => setImgError(true)}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-bordered" />
    )
  }
  return (
    <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm"
      style={{ background: getAvatarColor(contact?.userId || contact?.name) }}>
      {initial}
    </div>
  )
}

function GroupsPanel({ contacts, texts }, ref) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [sendingGroupId, setSendingGroupId] = useState(null)
  const [newName, setNewName] = useState("")
  const [creating, setCreating] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editId, setEditId] = useState(null) // group being renamed; null = create
  const [addingMember, setAddingMember] = useState(false)
  const [memberSearch, setMemberSearch] = useState("")

  // Let the parent (ContactsPanel header) trigger the create dialog, so the
  // "Add Group" button can live where "Add Contact" is.
  useImperativeHandle(ref, () => ({
    openCreate: () => { setEditId(null); setNewName(""); setShowCreate(true); setSelectedId(null) },
  }))

  const openEdit = (group) => {
    setEditId(group.groupId)
    setNewName(group.name || "")
    setShowCreate(true)
  }

  const contactsById = useMemo(() => {
    const m = new Map()
    for (const c of contacts || []) m.set(c.userId, c)
    return m
  }, [contacts])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listGroups()
      if (res.result) setGroups(res.groups || [])
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { load() }, [load])

  const selected = useMemo(
    () => groups.find((g) => g.groupId === selectedId) || null,
    [groups, selectedId]
  )

  const handleSaveGroup = async () => {
    const name = newName.trim()
    if (!name) return
    setCreating(true)
    try {
      if (editId) {
        const res = await renameGroup(editId, name)
        if (res.result) {
          setGroups((prev) => prev.map((g) => g.groupId === editId ? { ...g, name } : g))
          setShowCreate(false)
          setEditId(null)
          setNewName("")
        }
        return
      }
      const res = await addGroup(name)
      if (res.result && res.group) {
        setGroups((prev) => [...prev, res.group])
        setNewName("")
        setShowCreate(false)
      }
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteGroup = async (groupId) => {
    const group = groups.find((g) => g.groupId === groupId)
    const confirmed = await showPromptDialogAsync({
      title: texts?.deleteGroup,
      message: `${texts?.deleteGroupConfirm} "${group?.name || ""}"? ${texts?.deleteGroupNote}`,
      yesLabel: texts?.delete,
      noLabel: texts?.cancel,
    })
    if (!confirmed) return

    const res = await removeGroup(groupId)
    if (res.result) {
      setGroups((prev) => prev.filter((g) => g.groupId !== groupId))
      if (selectedId === groupId) setSelectedId(null)
    }
  }

  const handleAddMember = async (userId) => {
    const res = await addGroupMember(selected.groupId, userId)
    if (res.result) {
      setGroups((prev) => prev.map((g) =>
        g.groupId === selected.groupId
          ? { ...g, members: g.members.includes(userId) ? g.members : [...g.members, userId] }
          : g
      ))
    }
  }

  const handleRemoveMember = async (userId) => {
    const res = await removeGroupMember(selected.groupId, userId)
    if (res.result) {
      setGroups((prev) => prev.map((g) =>
        g.groupId === selected.groupId
          ? { ...g, members: g.members.filter((id) => id !== userId) }
          : g
      ))
    }
  }

  // Shared create/edit dialog — rendered from both the list and detail views.
  const groupDialog = showCreate ? (
    <Modal
      isOpen={showCreate}
      onClose={() => { setShowCreate(false); setEditId(null) }}
      title={editId ? texts?.editGroup : texts?.createGroup}
      variant="compact"
      className="max-w-sm"
      closeOnClickOutside
      closeOnEscape
    >
      <div className="p-5 flex flex-col gap-4">
        <input
          type="text"
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSaveGroup()}
          placeholder={texts?.groupNamePlaceholder}
          className="w-full rounded-xl border border-bordered bg-canvas px-3 py-2.5 text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary/50"
        />
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => { setShowCreate(false); setEditId(null) }} 
            className="flex-1"
          >
            {texts?.cancel}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveGroup} 
            disabled={creating || !newName.trim()} 
            className="flex-1"
          >
            {editId ? texts?.save : texts?.createGroupBtn}
          </Button>
        </div>
      </div>
    </Modal>
  ) : null

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // ── Group detail: members + add/remove ──────────────────────────────────────
  if (selected) {
    const memberIds = new Set(selected.members || [])
    const members = (selected.members || []).map((id) => contactsById.get(id) || { userId: id, name: id })
    const candidates = (contacts || []).filter((c) => {
      if (memberIds.has(c.userId)) return false
      const q = memberSearch.trim().toLowerCase()
      if (!q) return true
      return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)
    })

    return (
      <div className="flex flex-col gap-4">
        {groupDialog}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button onClick={() => { setSelectedId(null); setAddingMember(false); setMemberSearch("") }}
              className="p-1.5 rounded-lg hover:bg-black/5 text-text_secondary shrink-0">
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => openEdit(selected)}
              className="group flex items-center gap-2 flex-1 min-w-0 text-left"
              title={texts?.editGroup}
            >
              <Typography component="h3" variant="h4" className="truncate group-hover:text-primary">{selected.name}</Typography>
              <PencilSquareIcon className="w-5 h-5 text-text_secondary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>

          <Button
            variant="primary_s"
            onClick={() => setAddingMember((v) => !v)}
            className="w-full sm:w-auto"
          >
            <UserPlusIcon className="w-5 h-5" strokeWidth={2} />
            {texts?.addMember}
          </Button>
        </div>

        {/* Add member picker */}
        {addingMember && (
          <div className="bg-paper rounded-2xl border border-bordered p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Typography variant="txt1_accent">
                {texts?.addMember}
              </Typography>

              <button 
                onClick={() => { setAddingMember(false); setMemberSearch("") }}
                className="p-1 rounded-lg hover:bg-black/5 text-text_secondary"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <Input
              type="text"
              variant="transfer_compact"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder={texts?.searchPlaceholder}
            />

            <div className="max-h-64 overflow-y-auto -mx-1">
              {candidates.length === 0 ? (
                <Typography variant="txt2_dim" className="px-1 py-3 block">
                  {texts?.allContactsAdded}
                </Typography>
              ) : candidates.map((c) => (
                <button 
                  key={c.userId} 
                  onClick={() => handleAddMember(c.userId)}
                  className="w-full flex items-center gap-3 px-1 py-2 rounded-lg hover:bg-black/5 text-left"
                >
                  <Avatar contact={c} />

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text truncate"> 
                      {c.name || "N/A"}
                    </div>

                    {c.email && <div className="text-xs text-text_secondary truncate">
                      {c.email}
                    </div>}
                  </div>

                  <PlusCircleIcon className="w-6 h-6 text-primary stroke-2 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Members list */}
        {members.length === 0 ? (
          <div className="bg-canvas rounded-2xl p-10 text-center border border-bordered border-dashed">
            <UserGroupIcon className="w-10 h-10 text-text_secondary mx-auto mb-3" />

            <Typography variant="txt_dim">
              {texts?.noMembersNote}
            </Typography>
          </div>
        ) : (
          <div className="bg-paper rounded-2xl border border-bordered overflow-hidden">
            {members.map((m, i) => (
              <div key={m.userId}>
                {i > 0 && <div className="mx-5 border-t border-bordered" />}
                <div className="flex items-center gap-3 px-5 py-3">
                  <Avatar contact={m} />

                  <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                    <Typography 
                      variant="txt1_accent" 
                      className="text-text truncate leading-tight"
                    >
                      {m.name || "N/A"}
                    </Typography>

                    {m.email && <Typography 
                      variant="txt2_dim" 
                      className="leading-tight"
                    >
                      {m.email}
                    </Typography>}
                  </div>

                  <Button 
                    variant="icon" 
                    onClick={() => handleRemoveMember(m.userId)} 
                    className="hover:text-error" 
                    title={texts?.removeMember}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Groups list + create ────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">
      {groupDialog}

      {groups.length === 0 ? (
        <div className="bg-canvas rounded-2xl p-10 text-center border border-bordered border-dashed">
          <UserGroupIcon className="w-10 h-10 text-text_secondary mx-auto mb-3" />
          <Typography variant="txt_dim">{texts?.noGroupsNote}</Typography>
        </div>
      ) : (
        <DataTable
          data={groups}
          searchField="name"
          itemsPerPage={50}
          preferredHeight="auto"
          styleOverrides={{ header: "flex flex-row justify-left items-center mb-5", tableWrapper: "pt-2" }}
          noData={
            <div className="bg-canvas rounded-2xl p-8 text-center border border-bordered border-dashed">
              <Typography variant="txt_dim">{texts?.noGroupsFound}</Typography>
            </div>
          }
          renderRow={(g, i) => (
            <div key={g.groupId}>
              {i > 0 && <div className="mx-5 border-t border-bordered" />}
              {(() => {
                const groupEmails = [...new Set(
                  (g.members || [])
                    .map((id) => contactsById.get(id)?.email)
                    .filter(Boolean)
                    .map((e) => e.toLowerCase())
                )]
                const doSendGroup = () => {
                  if (!groupEmails.length || sendingGroupId) return
                  setSendingGroupId(g.groupId)
                  sessionStorage.setItem(
                    'transfer:initialAccess',
                    JSON.stringify({ emails: groupEmails, type: 'authenticated' }),
                  )
                  navigate('/')
                }
                return (
                  <div
                    className={`flex flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-3 px-5 py-3 transition-colors ${groupEmails.length ? 'cursor-pointer hover:bg-neutral_1/40' : ''}`}
                    onClick={doSendGroup}
                  >
                    <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto sm:flex-1">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <UserGroupIcon className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col flex-1 min-w-0 gap-0.5 text-left">
                        <Typography variant="txt1_accent" className="text-text truncate leading-tight">{g.name}</Typography>
                        <Typography variant="txt2_dim" className="leading-tight">
                          {(g.members?.length || 0)} {(g.members?.length === 1) ? texts?.member : texts?.members}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-auto sm:ml-0">
                      <Button
                        variant="icon"
                        disabled={sendingGroupId === g.groupId}
                        onClick={(e) => { e.stopPropagation(); doSendGroup() }}
                        className="hover:text-primary"
                        title={texts?.sendNow}
                      >
                        {sendingGroupId === g.groupId ? <Spinner variant="minimal" /> : <PaperAirplaneIcon className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="icon"
                        onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.groupId) }}
                        className="hover:text-error"
                        title={texts?.deleteGroup}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedId(g.groupId) }}
                        className="p-1 rounded-lg hover:bg-black/5 text-text_secondary"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
        />
      )}
    </div>
  )
}

export default forwardRef(GroupsPanel)
